import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TenantProvider } from "./contexts/TenantContext";
import { LoginPage } from "./components/LoginPage";
import { Layout } from "./components/Layout";
import { Dashboard } from "./components/Dashboard";
import { RequestList } from "./components/RequestList";
import { RequestDetail } from "./components/RequestDetail";
import { CreateRequest } from "./components/CreateRequest";
import { PaymentList } from "./components/PaymentList";
import { CatalogManagement } from "./components/CatalogManagement";
import { OpenRequests } from "./components/OpenRequests";
import LeaveManagement from "./components/LeaveManagement";
import AdminPanel from "./components/AdminPanel";
import AdminSettings from "./components/AdminSettings";
import UserManagement from "./components/UserManagement";
import AdminPerformanceDashboard from "./components/AdminPerformanceDashboard";
import QuotationBuilder from "./components/QuotationBuilder";
import PendingInvoices from "./components/PendingInvoices";
import OverduePaymentsList from "./components/OverduePaymentsList";
import PaymentAllocationView from "./components/PaymentAllocationView";
import CurrencyRateManagement from "./components/CurrencyRateManagement";
import CoverImageManagement from "./components/CoverImageManagement";
import CustomerManagement from "./components/CustomerManagement";
import { Toaster } from "./components/ui/sonner";
import { setupAutoLogout } from "./utils/autoLogout";
import { useNavigate } from "react-router-dom";

const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return <Layout>{children}</Layout>;
};

const LoginRedirect = () => {
    const { user } = useAuth();
    const location = useLocation();
    const from = location.state?.from || "/dashboard";

    if (user) {
        return <Navigate to={from} replace />;
    }

    return <LoginPage />;
};

const AppRoutes = () => {
    const { user, logout } = useAuth();
    const token = localStorage.getItem("token");
    const navigate = useNavigate();

    useEffect(() => {
        if (token) {
            setupAutoLogout(token, () => {
                logout();
            });
        }
    }, [token, navigate]);

    return (
        <Routes>
            <Route
                path="/login"
                element={<LoginRedirect />}
            />

            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/requests"
                element={
                    <ProtectedRoute>
                        <RequestList />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/new-request"
                element={
                    <ProtectedRoute>
                        <CreateRequest />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/requests/:id"
                element={
                    <ProtectedRoute>
                        <RequestDetail />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/payments"
                element={
                    <ProtectedRoute>
                        <PaymentList />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/catalog"
                element={
                    <ProtectedRoute>
                        <CatalogManagement />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/open-requests"
                element={
                    <ProtectedRoute>
                        <OpenRequests />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/leaves"
                element={
                    <ProtectedRoute>
                        <LeaveManagement />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/admin-panel"
                element={
                    <ProtectedRoute>
                        <AdminPanel />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/admin-settings"
                element={
                    <ProtectedRoute>
                        <AdminSettings />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/user-management"
                element={
                    <ProtectedRoute>
                        <UserManagement />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/customers"
                element={
                    <ProtectedRoute>
                        <CustomerManagement />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/admin-performance"
                element={
                    <ProtectedRoute>
                        <AdminPerformanceDashboard />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/quotation-builder"
                element={
                    <ProtectedRoute>
                        <QuotationBuilder />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/pending-invoices"
                element={
                    <ProtectedRoute>
                        <PendingInvoices />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/overdue-payments"
                element={
                    <ProtectedRoute>
                        <OverduePaymentsList />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/payment-allocations/:invoiceId"
                element={
                    <ProtectedRoute>
                        <PaymentAllocationView />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/cover-images"
                element={
                    <ProtectedRoute>
                        <CoverImageManagement />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/currency-rates"
                element={
                    <ProtectedRoute>
                        <CurrencyRateManagement />
                    </ProtectedRoute>
                }
            />

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
};

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <TenantProvider>
                    <BrowserRouter>
                        <AppRoutes />
                        <Toaster />
                    </BrowserRouter>
                </TenantProvider>
            </AuthProvider>
        </div>
    );
}

export default App;
